import { createAuthAxios, handleApiError } from "../../utils/apiUtils";
import { ROOMS_ENDPOINTS } from "../../config/apiConfig";

/**
 * Research Room Messaging Service
 * Handles all REST API calls for room messages
 */
class RoomMessageService {
  /**
   * Get messages for a room with cursor pagination
   * @param {String} roomId - Room ID
   * @param {String} cursor - Pagination cursor (optional)
   * @param {Number} limit - Number of messages to fetch (default: 50)
   */
  async getMessages(roomId, cursor = null, limit = 50) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const params = new URLSearchParams({ limit: limit.toString() });
      if (cursor) params.append("cursor", cursor);

      const url = `${ROOMS_ENDPOINTS.MESSAGES(roomId)}?${params.toString()}`;
      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Send a message to a room
   * @param {String} roomId - Room ID
   * @param {String} content - Message content
   * @param {String} replyToId - ID of message being replied to (optional)
   */
  async sendMessage(roomId, content, replyToId = null) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }
      if (!content || !content.trim()) {
        throw new Error("Message content is required");
      }

      // 🔍 DEBUG: Compare with task creation
      const user = JSON.parse(localStorage.getItem("user"));
const authAxios = createAuthAxios();
const response = await authAxios.post(
        ROOMS_ENDPOINTS.SEND_MESSAGE(roomId),
        {
          content: content.trim(),
          replyToId,
        }
      );
return response.data;
    } catch (error) {
throw handleApiError(error);
    }
  }

  /**
   * Edit a message
   * @param {String} roomId - Room ID
   * @param {String} messageId - Message ID
   * @param {String} newContent - New message content
   */
  async editMessage(roomId, messageId, newContent) {
    try {
      if (!roomId || !messageId) {
        throw new Error("Room ID and Message ID are required");
      }
      if (!newContent || !newContent.trim()) {
        throw new Error("Message content is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.put(
        ROOMS_ENDPOINTS.EDIT_MESSAGE(roomId, messageId),
        { content: newContent.trim() }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Delete a message
   * @param {String} roomId - Room ID
   * @param {String} messageId - Message ID
   */
  async deleteMessage(roomId, messageId) {
    try {
      if (!roomId || !messageId) {
        throw new Error("Room ID and Message ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        ROOMS_ENDPOINTS.DELETE_MESSAGE(roomId, messageId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Add a reaction to a message
   * @param {String} roomId - Room ID
   * @param {String} messageId - Message ID
   * @param {String} emoji - Emoji to add
   */
  async addReaction(roomId, messageId, emoji) {
    try {
      if (!roomId || !messageId || !emoji) {
        throw new Error("Room ID, Message ID, and emoji are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        ROOMS_ENDPOINTS.ADD_REACTION(roomId, messageId),
        { emoji }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Remove a reaction from a message
   * @param {String} roomId - Room ID
   * @param {String} messageId - Message ID
   * @param {String} emoji - Emoji to remove
   */
  async removeReaction(roomId, messageId, emoji) {
    try {
      if (!roomId || !messageId || !emoji) {
        throw new Error("Room ID, Message ID, and emoji are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        ROOMS_ENDPOINTS.ADD_REACTION(roomId, messageId),
        { data: { emoji } }
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Pin a message (Leader only)
   * @param {String} roomId - Room ID
   * @param {String} messageId - Message ID
   */
  async pinMessage(roomId, messageId) {
    try {
      if (!roomId || !messageId) {
        throw new Error("Room ID and Message ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(
        ROOMS_ENDPOINTS.PIN_MESSAGE(roomId, messageId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Unpin a message (Leader only)
   * @param {String} roomId - Room ID
   * @param {String} messageId - Message ID
   */
  async unpinMessage(roomId, messageId) {
    try {
      if (!roomId || !messageId) {
        throw new Error("Room ID and Message ID are required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.delete(
        ROOMS_ENDPOINTS.PIN_MESSAGE(roomId, messageId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get pinned messages for a room
   * @param {String} roomId - Room ID
   */
  async getPinnedMessages(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.PINNED_MESSAGES(roomId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get message context for jump-to-message feature
   * Returns 25 messages before + target + 25 messages after
   * @param {String} roomId - Room ID
   * @param {String} messageId - Target message ID
   * @param {Number} before - Number of messages before target (default: 25)
   * @param {Number} after - Number of messages after target (default: 25)
   */
  async getMessageContext(roomId, messageId, before = 25, after = 25) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }
      if (!messageId) {
        throw new Error("Message ID is required");
      }

      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        before: before.toString(),
        after: after.toString(),
      });

      const url = `${ROOMS_ENDPOINTS.MESSAGE_CONTEXT(
        roomId,
        messageId
      )}?${params.toString()}`;
      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Load more messages from a specific position (bidirectional scroll)
   * @param {String} roomId - Room ID
   * @param {String} cursor - Pagination cursor
   * @param {String} direction - 'older' or 'newer'
   * @param {Number} limit - Number of messages to fetch (default: 50)
   */
  async loadMoreFromPosition(roomId, cursor, direction = "older", limit = 50) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }
      if (!cursor) {
        throw new Error("Cursor is required");
      }

      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        cursor,
        direction,
        limit: limit.toString(),
      });

      const url = `${ROOMS_ENDPOINTS.LOAD_MORE(roomId)}?${params.toString()}`;
      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Search messages in a room
   * @param {String} roomId - Room ID
   * @param {String} query - Search query
   * @param {Number} limit - Number of results (default: 20)
   */
  async searchMessages(roomId, query, limit = 20) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }
      if (!query || !query.trim()) {
        throw new Error("Search query is required");
      }

      const authAxios = createAuthAxios();
      const params = new URLSearchParams({
        q: query.trim(),
        limit: limit.toString(),
      });

      const url = `${ROOMS_ENDPOINTS.SEARCH_MESSAGES(
        roomId
      )}?${params.toString()}`;
      const response = await authAxios.get(url);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Mark messages as read
   * @param {String} roomId - Room ID
   * @param {Array<String>} messageIds - Array of message IDs to mark as read (optional)
   */
  async markMessagesAsRead(roomId, messageIds = []) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.post(ROOMS_ENDPOINTS.MARK_READ(roomId), {
        messageIds,
      });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get unread message count for a room
   * @param {String} roomId - Room ID
   */
  async getUnreadCount(roomId) {
    try {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      const authAxios = createAuthAxios();
      const response = await authAxios.get(
        ROOMS_ENDPOINTS.UNREAD_COUNT(roomId)
      );
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export default new RoomMessageService();
